import type { AnalyticsConsent } from './preferences.js';

/**
 * Product analytics (founder feedback 2026-09-11, items 6 and 9): Amplitude for the flow, the
 * TikTok pixel for campaigns, one conversion — the first message a person sends.
 *
 * Three rules hold whatever the vendor:
 *
 * - **Nothing a person wrote or was told leaves as analytics.** Event names are a closed list and
 *   property values are closed codes, counts and booleans; there is no field a transcript could
 *   ride in. This is the frontend rule (allowlisted metadata only) applied to a third party.
 * - **Nothing is measured before consent.** No script is loaded, no request is made and no
 *   identifier is set until the person has said yes; saying no later unloads nothing that was
 *   never loaded and stops everything that was.
 * - **Keys are configuration, not source.** They arrive at build time from the environment;
 *   absent keys mean the module does nothing at all, which is also what every test sees.
 */
/**
 * What may be set on a person at the destination (founder, 2026-09-13: statistics). Closed codes
 * only — the gender and the four choices stated at registration — and never the name or the email.
 */
export interface UserTraits {
  readonly gender?: string | undefined;
  readonly ageRange?: string | undefined;
  readonly country?: string | undefined;
  readonly referral?: string | undefined;
  readonly interest?: string | undefined;
}

const TRAIT_KEYS = ['gender', 'ageRange', 'country', 'referral', 'interest'] as const;

/** Only the named keys, only string values: a widened object cannot smuggle anything onto the wire. */
export function closedTraits(traits: UserTraits): Readonly<Record<string, string>> {
  const out: Record<string, string> = {};
  for (const key of TRAIT_KEYS) {
    const value = traits[key];
    if (typeof value === 'string' && value.length > 0) out[key] = value;
  }
  return out;
}

export type AnalyticsEvent =
  | 'onboarding_started'
  | 'onboarding_step_viewed'
  | 'onboarding_completed'
  | 'onboarding_skipped'
  | 'signup_started'
  | 'signup_completed'
  | 'login'
  | 'logout'
  | 'conversation_created'
  | 'conversation_opened'
  | 'conversation_deleted'
  | 'conversations_erased'
  | 'message_sent'
  | 'first_message_sent'
  | 'reply_received'
  | 'reply_listened'
  | 'voice_recording_started'
  | 'voice_recording_sent'
  | 'voice_recording_reviewed'
  | 'voice_recording_cancelled'
  | 'interaction_used'
  | 'source_shown'
  | 'source_expanded'
  | 'memory_forgotten'
  | 'settings_changed'
  /** The experience survey was answered (founder, 2026-09-20); `wouldPay` is a closed code. */
  | 'survey_answered'
  | 'account_deleted'
  /** The welcome page's call to action that brought the person in (ADR-036); `cta` is a closed code. */
  | 'landing_cta_clicked';

/** Closed values only: a code, a count, a flag. Never free text. */
export type AnalyticsProperties = Readonly<Record<string, string | number | boolean>>;

export interface Analytics {
  track(event: AnalyticsEvent, properties?: AnalyticsProperties): void;
  /** The account, by opaque id, after sign-in; null on sign-out. Never an email. */
  /** The account by opaque id, and — once stated — the closed statistics choices; never the name or email. */
  identify(userId: string | null, traits?: UserTraits): void;
  /** Consent changed: start measuring, or stop and forget. */
  consentChanged(consent: AnalyticsConsent): void;
  /** True when at least one destination is configured; false means this module is inert. */
  readonly configured: boolean;
  /**
   * Install what consent allows, now, without waiting for an event to need it (founder,
   * 2026-09-20: "implement pixel everywhere"). The pixel used to appear only in the instant a
   * mapped conversion fired, so an ordinary visit reported nothing at all and TikTok saw
   * conversions with no landing traffic behind them.
   */
  start(): void;
}

/** The slice of the Amplitude browser SDK this module uses. Narrow so the fake stays faithful. */
export interface AmplitudeLike {
  init(apiKey: string, options: Readonly<Record<string, unknown>>): unknown;
  track(eventName: string, properties?: AnalyticsProperties): unknown;
  setUserId(userId: string | undefined): void;
  /** Closed user properties, set once known. */
  identify?: (traits: Readonly<Record<string, string>>) => void;
  reset(): void;
}

/** The TikTok pixel's global, as the pixel's own loader defines it. */
interface TtqLike {
  load(pixelId: string): void;
  page(): void;
  track(event: string, properties?: AnalyticsProperties): void;
}

/**
 * The conversion mapping (item 9). One product event becomes one standard pixel event; anything
 * not listed reaches the pixel as nothing at all.
 */
const TIKTOK_EVENTS: Readonly<Partial<Record<AnalyticsEvent, string>>> = Object.freeze({
  first_message_sent: 'SubmitForm',
  signup_completed: 'CompleteRegistration',
});

export interface AnalyticsConfig {
  readonly amplitudeApiKey: string | null;
  readonly tiktokPixelId: string | null;
  readonly consent: () => AnalyticsConsent | null;
  /** The SDK, loaded only once it is needed. Injected so tests never touch the network. */
  readonly loadAmplitude?: () => Promise<AmplitudeLike>;
  /** Installs the pixel loader into the page. Injected for the same reason. */
  readonly installTiktok?: (pixelId: string) => TtqLike | null;
}

/** Reads a build-time value: a non-empty string, or null. Never a default key. */
export function configuredKey(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

async function loadAmplitudeSdk(): Promise<AmplitudeLike> {
  const sdk = await import('@amplitude/analytics-browser');
  return {
    init: (apiKey, options) => sdk.init(apiKey, options),
    track: (eventName, properties) => sdk.track(eventName, properties),
    setUserId: (userId) => {
      sdk.setUserId(userId);
    },
    identify: (traits) => {
      const identify = new sdk.Identify();
      for (const [key, value] of Object.entries(traits)) identify.set(key, value);
      sdk.identify(identify);
    },
    reset: () => {
      sdk.reset();
    },
  };
}

/**
 * The pixel's documented loader, reduced to what it does: define the `ttq` queue, then fetch the
 * pixel script for this id. The copied snippet from the ads manager does the same with a longer
 * method list; only the methods this module calls are stubbed, and the script fills in the rest.
 */
function installTiktokPixel(pixelId: string): TtqLike | null {
  const doc = globalThis.document;
  if (typeof doc === 'undefined') return null;
  const w = globalThis as unknown as { TiktokAnalyticsObject?: string; ttq?: unknown };
  w.TiktokAnalyticsObject = 'ttq';
  const queue: unknown[][] & Partial<TtqLike> = [] as unknown[][] & Partial<TtqLike>;
  const defer =
    (method: string) =>
    (...args: unknown[]): void => {
      queue.push([method, ...args]);
    };
  for (const method of ['page', 'track', 'identify', 'load']) {
    (queue as unknown as Record<string, unknown>)[method] = defer(method);
  }
  w.ttq = queue;
  const script = doc.createElement('script');
  script.async = true;
  script.src = `https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=${encodeURIComponent(pixelId)}&lib=ttq`;
  doc.head.appendChild(script);
  return queue as unknown as TtqLike;
}

export function createAnalytics(config: AnalyticsConfig): Analytics {
  const loadAmplitude = config.loadAmplitude ?? loadAmplitudeSdk;
  const installTiktok = config.installTiktok ?? installTiktokPixel;
  const configured = config.amplitudeApiKey !== null || config.tiktokPixelId !== null;

  let amplitude: Promise<AmplitudeLike> | null = null;
  let ttq: TtqLike | null = null;
  let userId: string | null = null;
  /** Events that arrived while the SDK was still loading; replayed in order, never dropped. */
  const granted = (): boolean => configured && config.consent() === 'granted';

  const amplitudeReady = (): Promise<AmplitudeLike> | null => {
    if (config.amplitudeApiKey === null) return null;
    if (amplitude === null) {
      const key = config.amplitudeApiKey;
      amplitude = loadAmplitude().then((sdk) => {
        // No autocapture: the pages, clicks and forms it would record on its own are exactly the
        // surface a transcript could leak through. Every event is named here, deliberately.
        sdk.init(key, { autocapture: false, defaultTracking: false });
        if (userId !== null) sdk.setUserId(userId);
        return sdk;
      });
    }
    return amplitude;
  };

  const tiktokReady = (): TtqLike | null => {
    if (config.tiktokPixelId === null) return null;
    if (ttq === null) {
      ttq = installTiktok(config.tiktokPixelId);
      ttq?.load(config.tiktokPixelId);
      ttq?.page();
    }
    return ttq;
  };

  return {
    configured,
    start() {
      if (!granted()) return;
      tiktokReady();
    },
    track(event, properties = {}) {
      if (!granted()) return;
      void amplitudeReady()?.then((sdk) => sdk.track(event, properties));
      const pixelEvent = TIKTOK_EVENTS[event];
      if (pixelEvent !== undefined) tiktokReady()?.track(pixelEvent);
    },
    identify(id, traits) {
      userId = id;
      if (!granted()) return;
      void amplitudeReady()?.then((sdk) => {
        if (id === null) {
          sdk.setUserId(undefined);
          sdk.reset();
          return;
        }
        sdk.setUserId(id);
        if (traits !== undefined && sdk.identify !== undefined) sdk.identify(closedTraits(traits));
      });
    },
    consentChanged(consent) {
      if (consent === 'granted') {
        void amplitudeReady();
        tiktokReady();
        return;
      }
      // Denied: forget the person on the destinations already loaded. Nothing new is sent.
      void amplitude?.then((sdk) => {
        sdk.setUserId(undefined);
        sdk.reset();
      });
    },
  };
}

/** The inert module: every call is a no-op. What tests and unconfigured builds use. */
export const NO_ANALYTICS: Analytics = Object.freeze({
  configured: false,
  start: () => undefined,
  track: () => undefined,
  identify: () => undefined,
  consentChanged: () => undefined,
});
